"use client";

import React, { useState, useEffect } from "react";
import MainLayout from "@/components/layouts/main-layout";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";

type LegalSection = {
  title: string;
  content: string | string[];
};

type LegalPageProps = {
  title: string;
  lastUpdated: string;
  intro: string;
  sections: LegalSection[];
};

const linkify = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s,)]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-4 hover:text-theme transition-colors duration-200">{part}</a>
    ) : part
  );
};

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/ø/g, "o")
    .replace(/å/g, "aa")
    .replace(/æ/g, "ae")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const LegalPage = ({ title, lastUpdated, intro, sections }: LegalPageProps) => {
  const t = useTranslations("legal");
  const cs = useTranslations("caseStudy");
  const tA11y = useTranslations("accessibility");
  const firstId = sections.length > 0 ? slugify(sections[0].title) : "";
  const [activeId, setActiveId] = useState(firstId);

  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace("#", "");
      setActiveId(hash || firstId);
    };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [firstId]);

  return (
    <MainLayout>
      <div className="pt-[110px] md:pt-[140px] lg:pt-[180px] pb-[60px] lg:pb-[120px] relative">
        {/* Background gradient — matching case study style */}
        <div className="absolute top-0 left-0 right-0 h-[680px] z-0">
          <span className="absolute inline-block w-[514px] h-[514px] bg-[linear-gradient(180deg,_#A9A8BF_0%,_#060519_100%)] opacity-25 blur-[175px] z-2 rounded-full top-[-15%] start-[-10%]"></span>
          <Image
            src="/assets/backgrounds/case-style-bg-head.webp"
            width={1440}
            height={701}
            loading="eager"
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover object-[0%_center]"
            alt="Page header background"
            aria-hidden="true"
          />
          <span className="absolute z-0 w-full h-full bg-[#6A4A8F] opacity-75 mix-blend-multiply"></span>
          <span className="absolute z-1 w-full h-full bg-[linear-gradient(180deg,_rgba(10,0,31,0.1)_0%,_rgba(10,0,31,0.6)_50%,_#0A001F_100%)]"></span>
        </div>

        <div className="absolute right-[-5%] top-[10%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none" />

        <article className="relative z-1">
          <div className="container lg:max-w-[1100px]">
            {/* Breadcrumb */}
            <div className="mb-6">
              <nav
                aria-label={tA11y("breadcrumb")}
                className="flex items-center gap-2 text-[13px] lg:text-[14px] text-foreground/40"
              >
                <Link href="/" className="hover:text-theme transition-colors duration-300">
                  {cs("home")}
                </Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-foreground/90">{title}</span>
              </nav>
            </div>

            {/* Heading */}
            <div>
              <h1 className="text-[30px] sm:text-[38px] md:text-[48px] lg:text-[56px] font-semibold leading-[1.2] mb-[16px] max-w-[680px]">
                {title}
              </h1>
            </div>

            <div>
              <p className="text-[13px] lg:text-[14px] font-light text-foreground/50 tracking-[0.02em] mb-[32px] lg:mb-[48px]">
                {t("lastUpdated")}: {lastUpdated}
              </p>
            </div>

            {/* Intro */}
            <div>
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.8] text-foreground/90 mb-[40px] lg:mb-[56px] max-w-[680px]">
                {intro}
              </p>
            </div>

            {/* Content grid — text + sticky index */}
            <div className="lg:grid lg:grid-cols-[1fr_240px] lg:gap-[60px]">
              {/* Main content */}
              <div className="md:max-w-[620px] lg:max-w-[760px]">
                <div className="space-y-[36px] lg:space-y-[48px]">
                  {sections.map((section, i) => (
                    <div key={i}>
                      <section id={slugify(section.title)}>
                        <h2 className="text-[20px] lg:text-[24px] font-semibold leading-[1.3] mb-[14px] lg:mb-[18px]">
                          {section.title}
                        </h2>
                        {Array.isArray(section.content) ? (
                          <div className="space-y-[12px]">
                            {section.content.map((paragraph, j) => (
                              <p
                                key={j}
                                className="text-[15px] lg:text-[16px] font-light leading-[1.8] text-foreground/90"
                              >
                                {linkify(paragraph)}
                              </p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[15px] lg:text-[16px] font-light leading-[1.8] text-foreground/90">
                            {linkify(section.content)}
                          </p>
                        )}
                      </section>
                    </div>
                  ))}
                </div>

                {/* Footer note */}
                <div>
                  <div className="mt-[48px] lg:mt-[64px] pt-[24px] border-t border-white/[0.06]">
                    <p className="text-[13px] font-light text-foreground/90 leading-[1.7]">
                      {t("contactNote")}{" "}
                      <a
                        href="mailto:legal@flyingcode.eu"
                        className="text-foreground underline underline-offset-4 decoration-foreground/70 hover:text-foreground/90 hover:decoration-foreground transition-colors duration-200"
                      >
                        legal@flyingcode.eu
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Sticky index — desktop only */}
              <div className="hidden lg:block">
                <div className="sticky top-[140px]">
                  <div>
                    <AnimatedCard
                      className="rounded-[16px] lg:rounded-[20px]"
                      circleClassName="w-[120px] h-[120px] blur-[80px]"
                    >
                      <BorderWrapper
                        wrapperClass="rounded-[16px] lg:rounded-[20px]"
                        className="rounded-[16px] lg:rounded-[20px] bg-gradient-to-b from-[#121124] to-[#0e0821] p-[24px] relative overflow-hidden"
                      >
                        <p className="text-[11px] font-semibold text-foreground/30 uppercase tracking-[0.16em] mb-[16px]">
                          {t("onThisPage")}
                        </p>
                        <nav aria-label={t("onThisPage")} className="space-y-[2px]">
                          {sections.map((section, i) => {
                            const label = section.title.replace(/^\d+\.\s*/, "");
                            const id = slugify(section.title);
                            const isActive = activeId === id;
                            return (
                              <a
                                key={i}
                                href={`#${id}`}
                                className={`block text-[13px] font-light leading-[1.4] py-[10px] px-[12px] rounded-[10px] transition-all duration-300 ${
                                  isActive
                                    ? "text-white bg-[linear-gradient(180deg,_#3C1C63_0%,_rgba(60,_28,_99,_0)_100%)]"
                                    : "text-foreground/40 hover:text-white hover:bg-[linear-gradient(180deg,_#3C1C63_0%,_rgba(60,_28,_99,_0)_100%)]"
                                }`}
                              >
                                {label}
                              </a>
                            );
                          })}
                        </nav>
                      </BorderWrapper>
                    </AnimatedCard>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </MainLayout>
  );
};

export default LegalPage;
